export const learningSections = [
  {
    id: 'traffic-control',
    icon: '🚦',
    title: 'Traffic Control',
    summary: 'Decode signals, signs, and lane markings so you move with confidence.',
    focus: 'Signal literacy and right-of-way cues.',
    intro: 'Traffic control is the language of the road. Learn to read red and green lights, understand what arrows mean, interpret road signs, and follow lane markings. These visual cues protect everyone on the road and are critical for passing your test.',
    lessons: [
      {
        id: 'tc-lesson-1',
        title: 'Understanding Traffic Lights',
        duration: '10 min',
        content: [
          {
            type: 'concept',
            heading: 'Traffic Light Basics',
            text: 'Traffic lights control the flow of vehicles and pedestrians at intersections. Each color has a specific meaning that you must follow.'
          },
          {
            type: 'keypoints',
            points: [
              '🔴 Red Light: Complete stop required before the stop line or crosswalk',
              '🟡 Yellow Light: Prepare to stop - light is about to turn red',
              '🟢 Green Light: Proceed when safe, but yield to pedestrians and vehicles clearing the intersection',
              '➡️ Green Arrow: Protected turn - oncoming traffic is stopped',
              '⚠️ Flashing Red: Treat as a stop sign - stop, then proceed when safe',
              '⚠️ Flashing Yellow: Slow down and proceed with caution'
            ]
          },
          {
            type: 'example',
            scenario: 'You approach an intersection with a solid red light',
            action: '1. Begin slowing down early\n2. Stop completely before the white stop line\n3. Check for pedestrians in the crosswalk\n4. Wait for the green light or green arrow\n5. Never enter on red unless turning right where permitted'
          },
          {
            type: 'reminder',
            text: 'Always come to a complete stop - "rolling stops" are illegal and dangerous.'
          }
        ]
      },
      {
        id: 'tc-lesson-2',
        title: 'Road Signs and Their Meanings',
        duration: '15 min',
        content: [
          {
            type: 'concept',
            heading: 'Types of Road Signs',
            text: 'Road signs are categorized by shape and color. Each category serves a different purpose to guide and protect road users.'
          },
          {
            type: 'keypoints',
            points: [
              '🛑 Regulatory Signs (Red/White): Must obey - Stop, Yield, Speed Limit, No Entry',
              '⚠️ Warning Signs (Yellow/Black): Alert you to hazards - Curves, School Zone, Pedestrian Crossing',
              'ℹ️ Information Signs (Blue/White/Green): Provide helpful information - Directions, Services, Exit Numbers',
              '🚧 Construction Signs (Orange/Black): Temporary warnings in work zones'
            ]
          },
          {
            type: 'example',
            scenario: 'Common Signs You Must Know',
            action: 'STOP Sign (Octagon): Come to complete stop\nYIELD Sign (Triangle): Slow down, give way to other traffic\nSPEED LIMIT: Maximum legal speed\nSCHOOL ZONE: Reduced speed when children present\nPEDESTRIAN CROSSING: Watch for people crossing'
          },
          {
            type: 'reminder',
            text: 'Sign shapes are designed so you can recognize them even if covered in snow or dirt.'
          }
        ]
      },
      {
        id: 'tc-lesson-3',
        title: 'Lane Markings and Road Lines',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'Understanding Lane Markings',
            text: 'Lane markings guide traffic flow and indicate where you can or cannot drive, pass, or change lanes.'
          },
          {
            type: 'keypoints',
            points: [
              '⚪ White Lines: Separate traffic going the same direction',
              '🟡 Yellow Lines: Separate traffic going opposite directions',
              '━━━ Solid Line: Do not cross or pass',
              '- - - Dashed Line: You may cross or pass when safe',
              '━ ━ Solid + Dashed: Can cross only from the dashed side',
              '━━ ━━ Double Solid: No passing from either direction'
            ]
          },
          {
            type: 'example',
            scenario: 'You see a solid yellow line on your side and a dashed yellow line on the other side',
            action: 'The solid line means YOU cannot pass. Vehicles on the other side (with the dashed line) may pass if safe.'
          },
          {
            type: 'reminder',
            text: 'Crossing solid lines is illegal and can result in head-on collisions. Always look for dashed lines before passing.'
          }
        ]
      },
      {
        id: 'tc-lesson-4',
        title: 'Right-of-Way Rules',
        duration: '15 min',
        content: [
          {
            type: 'concept',
            heading: 'Who Goes First?',
            text: 'Right-of-way rules prevent confusion and crashes. Knowing who should go first at intersections is critical for safety.'
          },
          {
            type: 'keypoints',
            points: [
              '🚦 At controlled intersections: Follow traffic signals and signs',
              '🛑 At 4-way stops: First to arrive goes first; if tied, vehicle on right goes first',
              '➡️ When turning: Yield to oncoming traffic and pedestrians',
              '👥 Pedestrians: ALWAYS have right-of-way in crosswalks',
              '🚑 Emergency vehicles: Pull over and stop for vehicles with lights and sirens',
              '🚌 School buses: Stop for buses with flashing red lights'
            ]
          },
          {
            type: 'example',
            scenario: 'You and another car arrive at a 4-way stop at the same time',
            action: '1. Make eye contact with the other driver\n2. If they are to your right, yield to them\n3. If you are making a left turn and they are going straight, they have priority\n4. Never assume - be prepared to yield even if you think you have the right-of-way'
          },
          {
            type: 'reminder',
            text: 'Right-of-way is something you GIVE, not something you TAKE. When in doubt, be courteous and yield.'
          }
        ]
      },
      {
        id: 'tc-lesson-5',
        title: 'Special Traffic Control Situations',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'Advanced Traffic Control Scenarios',
            text: 'Some situations require special attention and understanding beyond basic traffic lights and signs.'
          },
          {
            type: 'keypoints',
            points: [
              '🚧 Lane Control Signals: Red X means lane closed; Green arrow means lane open',
              '⚡ Reversible Lanes: Direction changes based on time of day',
              '🚥 Advanced Green: Oncoming traffic faces red - you can turn left freely',
              '🟡 Pedestrian Crossovers: Flashing lights - must stop for pedestrians',
              '🔄 Traffic Circles/Roundabouts: Yield to traffic already in the circle'
            ]
          },
          {
            type: 'example',
            scenario: 'You approach a roundabout for the first time',
            action: '1. Slow down as you approach\n2. Yield to pedestrians in crosswalks\n3. Yield to vehicles already circulating\n4. Enter when there is a safe gap\n5. Stay in your lane - do not change lanes inside\n6. Signal right before your exit\n7. Exit safely, watching for pedestrians'
          },
          {
            type: 'reminder',
            text: 'Never stop inside a roundabout. If you miss your exit, go around again.'
          }
        ]
      }
    ],
    questions: [
      {
        id: 'traffic-control-1',
        prompt: 'What should you do at a solid red light with a marked stop line?',
        options: [
          'Coast slowly if the road looks clear',
          'Stop before the line, scan for people, and wait for green or arrow',
          'Stop after the crosswalk so you can see better',
          'Proceed if cross traffic is light'
        ],
        answerIndex: 1,
        explanation: 'A complete stop before the line protects people in the crosswalk and preserves your sightlines.'
      },
      {
        id: 'traffic-control-2',
        prompt: 'A flashing yellow light at a mid-block crosswalk means you should',
        options: [
          'Accelerate to clear the area',
          'Stop only if a pedestrian steps on the road',
          'Slow, be prepared to stop, and yield to pedestrians',
          'Treat it like a green light'
        ],
        answerIndex: 2,
        explanation: 'Flashing yellow is a caution; you must yield to people in or entering the crosswalk.'
      },
      {
        id: 'traffic-control-3',
        prompt: 'When a lane control signal shows a red X above your lane on a reversible road you must',
        options: [
          'Merge out of that lane as soon as it is safe',
          'Continue until you see a police officer',
          'Use the shoulder until the next exit',
          'Slow to 30 km/h and stay in the lane'
        ],
        answerIndex: 0,
        explanation: 'A red X means the lane is closed to you; merge out before you enter the controlled area.'
      },
      {
        id: 'traffic-control-4',
        prompt: 'At a four-way stop that you reached first you should',
        options: [
          'Wave other drivers through to be polite',
          'Proceed after a full stop when safe to do so',
          'Wait until at least two cars go first',
          'Roll forward to claim the right-of-way'
        ],
        answerIndex: 1,
        explanation: 'First to stop is first to go, but only after a complete stop and when the path is clear.'
      },
      {
        id: 'traffic-control-5',
        prompt: 'A pedestrian is waiting at an unmarked crosswalk at an intersection. You should',
        options: [
          'Keep speed because there is no paint',
          'Stop only if a sign says to stop',
          'Yield; intersections include unmarked crosswalks',
          'Tap the horn and pass'
        ],
        answerIndex: 2,
        explanation: 'Intersections are legal crosswalks even without paint; yield to people crossing.'
      }
    ]
  },
  {
    id: 'basics',
    icon: '🚗',
    title: 'Basics of Driving',
    summary: 'Build rock-solid habits for starting, scanning, spacing, and stopping.',
    focus: 'Vehicle setup, vision, and speed control.',
    intro: 'Before you can drive safely anywhere, you need solid fundamentals. This section covers how to position yourself for comfort and control, how to scan mirrors and the road ahead, how to keep a safe distance, and how to brake smoothly. Master these basics and everything else becomes easier.',
    lessons: [
      {
        id: 'bd-lesson-1',
        title: 'Pre-Drive Vehicle Setup',
        duration: '10 min',
        content: [
          {
            type: 'concept',
            heading: 'Getting Ready to Drive',
            text: 'Proper vehicle setup before you start the engine is essential for safety and control. Never skip these steps.'
          },
          {
            type: 'keypoints',
            points: [
              '💺 Adjust the seat: Reach pedals comfortably with slight bend in knees',
              '🪞 Adjust mirrors: See behind and minimize blind spots',
              '🔧 Steering wheel: Should be at chest level, arms slightly bent',
              '🔒 Seatbelt: Fasten BEFORE starting the engine',
              '🎛️ Check dashboard: Warning lights, fuel level, temperature',
              '👀 Final check: Ensure parking brake is on, transmission in Park/Neutral'
            ]
          },
          {
            type: 'example',
            scenario: 'Setting up before your first drive',
            action: '1. Enter vehicle and close door\n2. Adjust seat forward/back and up/down\n3. Adjust steering wheel height if available\n4. Fasten seatbelt properly across chest and lap\n5. Adjust rearview mirror to see entire rear window\n6. Adjust side mirrors to see blind spot area\n7. Check all around vehicle before starting'
          },
          {
            type: 'reminder',
            text: 'Never adjust mirrors or seat while the vehicle is moving. Do it all before you shift into gear.'
          }
        ]
      },
      {
        id: 'bd-lesson-2',
        title: 'Scanning and Visual Awareness',
        duration: '15 min',
        content: [
          {
            type: 'concept',
            heading: 'Where to Look While Driving',
            text: 'Good drivers constantly scan their environment. Your eyes should never stay in one place for more than 2 seconds.'
          },
          {
            type: 'keypoints',
            points: [
              '👁️ Look 12-15 seconds ahead: Spot hazards early',
              '🪞 Check mirrors every 5-8 seconds: Know what\'s around you',
              '↔️ Scan side to side: Watch intersections and driveways',
              '🎯 Use the "2-4-12 second rule": Check 2 sec (immediate), 4 sec (mid-range), 12 sec (far ahead)',
              '⚠️ Identify escape routes: Always know where you could go in an emergency',
              '🚗 Check blind spots: Turn head to look over shoulder before lane changes'
            ]
          },
          {
            type: 'example',
            scenario: 'Proper scanning while driving on a city street',
            action: '1. Look ahead 12-15 seconds (about 1 block)\n2. Check rearview mirror\n3. Scan left intersection\n4. Check right side mirror\n5. Look ahead again\n6. Check left side mirror\n7. Repeat cycle every 5-8 seconds'
          },
          {
            type: 'reminder',
            text: 'Aim high in steering - look where you want to go, not just at the car ahead. Your hands follow your eyes.'
          }
        ]
      },
      {
        id: 'bd-lesson-3',
        title: 'Safe Following Distance',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'The Two-Second Rule',
            text: 'Maintaining proper space between you and the vehicle ahead gives you time to react and stop safely.'
          },
          {
            type: 'keypoints',
            points: [
              '⏱️ Two-Second Rule: Pick a fixed object (sign, pole), when front vehicle passes it, you should pass it 2+ seconds later',
              '🌧️ Bad weather: Increase to 4 seconds in rain, 8-10 seconds in snow/ice',
              '🚛 Following large vehicles: Add 1 extra second - they block your view',
              '🌙 Night driving: Add 1 extra second - reduced visibility',
              '⚡ If tailgated: Increase your following distance to compensate',
              '📏 General rule: 1 vehicle length per 10 km/h of speed'
            ]
          },
          {
            type: 'example',
            scenario: 'Using the two-second rule on a highway',
            action: '1. Watch the vehicle ahead pass a landmark (bridge, sign)\n2. Begin counting: "One thousand one, one thousand two"\n3. You should NOT reach the landmark before finishing the count\n4. If you do, you\'re too close - slow down and recheck\n5. In rain, count to 4 instead of 2'
          },
          {
            type: 'reminder',
            text: 'Space is your safety margin. The faster you go, the more space you need. Never tailgate.'
          }
        ]
      },
      {
        id: 'bd-lesson-4',
        title: 'Smooth Braking and Stopping',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'How to Brake Properly',
            text: 'Smooth braking is safer, more comfortable, and extends the life of your vehicle. Hard braking should be reserved for emergencies only.'
          },
          {
            type: 'keypoints',
            points: [
              '👟 Cover the brake: Move foot over brake pedal when approaching hazards',
              '📉 Progressive braking: Gentle pressure, increase gradually, ease off at the end',
              '🛑 Complete stops: Continue braking until vehicle is fully stopped, then hold',
              '⛰️ Downhill: Brake earlier and more gently',
              '❄️ Slippery conditions: Pump brakes gently if no ABS; steady pressure with ABS',
              '⚠️ Emergency: Brake hard and fast while steering if ABS equipped'
            ]
          },
          {
            type: 'example',
            scenario: 'Stopping at a red light',
            action: '1. See the red light early (scan ahead)\n2. Check mirrors - know what\'s behind you\n3. Release accelerator\n4. Cover brake pedal\n5. Apply gentle, increasing pressure\n6. Stop smoothly before the stop line\n7. Ease pressure slightly as you stop to avoid jerking\n8. Keep foot on brake until light turns green'
          },
          {
            type: 'reminder',
            text: 'If passengers feel the vehicle "nod" forward when you stop, you\'re braking too hard at the end. Ease off the pedal as you come to rest.'
          }
        ]
      },
      {
        id: 'bd-lesson-5',
        title: 'Steering and Vehicle Control',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'Proper Steering Techniques',
            text: 'How you hold and turn the steering wheel affects your control and reaction time in emergencies.'
          },
          {
            type: 'keypoints',
            points: [
              '🕐 Hand position: 9 and 3 o\'clock (or 10 and 2)',
              '🎯 Push-pull steering: Push up with one hand, pull down with the other',
              '🔄 Hand-over-hand: For tight turns and parking',
              '👀 Look where you\'re going: Vehicle goes where eyes go',
              '↩️ Reverse: Look over shoulder, use one hand on wheel',
              '⚡ Keep thumbs on outside: Prevents injury if wheel kicks back'
            ]
          },
          {
            type: 'example',
            scenario: 'Making a left turn at an intersection',
            action: '1. Signal early (at least 30 meters before turn)\n2. Check mirrors and blind spot\n3. Position in left lane\n4. Slow to appropriate speed (15-20 km/h for 90° turn)\n5. Look through the turn where you want to go\n6. Turn wheel smoothly using push-pull method\n7. Accelerate gently as you complete the turn\n8. Straighten wheel, cancel signal'
          },
          {
            type: 'reminder',
            text: 'Never let the wheel "spin" through your hands. Always maintain control with both hands on the wheel.'
          }
        ]
      },
      {
        id: 'bd-lesson-6',
        title: 'Starting, Stopping, and Parking',
        duration: '15 min',
        content: [
          {
            type: 'concept',
            heading: 'Basic Vehicle Maneuvers',
            text: 'Every trip begins and ends with starting and parking. Master these fundamentals to drive confidently anywhere.'
          },
          {
            type: 'keypoints',
            points: [
              '🔑 Starting: Foot on brake, start engine, check surroundings, release parking brake, shift to D',
              '🅿️ Parallel parking: Signal, stop beside space, reverse while turning, straighten, adjust',
              '⬅️ Backing up: Look over shoulder, use mirrors, move slowly, stop frequently to recheck',
              '🏔️ Hill parking: Turn wheels to curb (down) or from curb (up), set parking brake',
              '⛔ Parking brake: Always use when parked, especially on hills',
              '🚪 Exit safely: Check for bikes/traffic before opening door'
            ]
          },
          {
            type: 'example',
            scenario: 'Parallel parking step-by-step',
            action: '1. Find a space at least 1.5× your vehicle length\n2. Pull alongside front car, 30 cm away\n3. Signal right, check traffic and mirrors\n4. Shift to reverse, look over right shoulder\n5. Slowly back while turning wheel fully right\n6. When your vehicle is at 45°, straighten wheel\n7. Continue backing straight\n8. When rear is clear, turn wheel fully left\n9. Straighten and adjust position\n10. Shift to Park, engage parking brake'
          },
          {
            type: 'reminder',
            text: 'Practice makes perfect. Find an empty parking lot and practice these maneuvers until they feel natural.'
          }
        ]
      }
    ],
    questions: [
      {
        id: 'basics-1',
        prompt: 'Before moving the vehicle the best sequence is',
        options: [
          'Start engine, release brake, then adjust mirrors',
          'Adjust seat and mirrors, fasten belt, then start',
          'Fasten belt after merging',
          'Start engine and adjust mirrors while rolling'
        ],
        answerIndex: 1,
        explanation: 'Set position and mirrors first so you can see and reach the controls before you move.'
      },
      {
        id: 'basics-2',
        prompt: 'The two-second rule helps you',
        options: [
          'Choose the right gear for hills',
          'Set a safe following distance',
          'Estimate fuel range',
          'Check your blind spot'
        ],
        answerIndex: 1,
        explanation: 'Picking a landmark two seconds ahead keeps space to react at any legal speed.'
      },
      {
        id: 'basics-3',
        prompt: 'Good scanning means',
        options: [
          'Looking only straight ahead to avoid distraction',
          'Checking mirrors every 5-8 seconds and far ahead',
          'Watching only the nearest car',
          'Relying on alerts from other drivers'
        ],
        answerIndex: 1,
        explanation: 'Frequent mirror checks plus far-ahead vision spot risks before they reach you.'
      },
      {
        id: 'basics-4',
        prompt: 'Smooth braking is easiest when you',
        options: [
          'Brake late with more pressure',
          'Cover the brake early and ease on pressure',
          'Downshift and skip braking',
          'Turn the wheel while braking hard'
        ],
        answerIndex: 1,
        explanation: 'Early, gentle braking keeps weight balanced and passengers comfortable.'
      },
      {
        id: 'basics-5',
        prompt: 'Backing out of a driveway, you should',
        options: [
          'Rely on the camera only',
          'Check mirrors, shoulder check, and move slowly',
          'Reverse quickly to clear the lane',
          'Skip shoulder checks if no sidewalks'
        ],
        answerIndex: 1,
        explanation: 'Combine mirrors, cameras, and shoulder checks; move slowly so you can stop for people or bikes.'
      }
    ]
  },
  {
    id: 'highways',
    icon: '🛣️',
    title: 'Highways and Freeways',
    summary: 'Merge, cruise, and exit smoothly while keeping room to spare.',
    focus: 'Speed management and space cushions.',
    intro: "High-speed driving demands new skills. You'll learn how to merge safely into traffic flow, how to adjust following distance for speed, how to handle weather challenges on freeways, and how to exit without drama. These techniques keep you and others safe at highway speeds.",
    lessons: [
      {
        id: 'hw-lesson-1',
        title: 'Entering and Merging onto Highways',
        duration: '15 min',
        content: [
          {
            type: 'concept',
            heading: 'Safe Highway Entry',
            text: 'Merging onto a highway requires matching the speed of traffic and finding a safe gap. Never stop on an acceleration ramp unless absolutely necessary.'
          },
          {
            type: 'keypoints',
            points: [
              '🚗 Use the acceleration lane: Speed up to match highway traffic (usually 80-100 km/h)',
              '👀 Check mirrors and blind spot: Find a gap in traffic',
              '➡️ Signal early: Let highway drivers know your intention',
              '⚡ Merge smoothly: Match speed and slide into the gap',
              '❌ Never stop: Unless traffic is completely stopped ahead',
              '🎯 Aim for the right lane: Merge into rightmost through lane'
            ]
          },
          {
            type: 'example',
            scenario: 'Merging onto a busy highway',
            action: '1. Enter acceleration ramp at 50-60 km/h\n2. Accelerate firmly while checking traffic\n3. Signal right\n4. Check left mirror and blind spot\n5. Identify a safe gap (4+ seconds)\n6. Accelerate to match traffic speed\n7. Merge smoothly when gap arrives\n8. Cancel signal, maintain speed'
          },
          {
            type: 'reminder',
            text: 'Highway drivers are not required to let you in. It\'s YOUR responsibility to find a safe gap and merge.'
          }
        ]
      },
      {
        id: 'hw-lesson-2',
        title: 'Highway Lane Selection and Passing',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'Choosing the Right Lane',
            text: 'Different lanes serve different purposes on multi-lane highways. Understanding lane discipline prevents conflicts.'
          },
          {
            type: 'keypoints',
            points: [
              '➡️ Right lane: For slower traffic, entering, and exiting',
              '↔️ Center lanes: For through traffic at normal speed',
              '⬅️ Left lane: For passing and faster traffic (keep right except to pass)',
              '🚫 Never pass on the right: Unless in slow traffic or vehicle is turning left',
              '👁️ Before changing lanes: Mirror, signal, blind spot check, move smoothly',
              '⏱️ Complete passes quickly: Don\'t linger beside other vehicles'
            ]
          },
          {
            type: 'example',
            scenario: 'Passing a slower vehicle on the highway',
            action: '1. Check that passing is safe and legal\n2. Check left mirror and blind spot\n3. Signal left\n4. Move into passing lane smoothly\n5. Accelerate to pass (within speed limit)\n6. Keep safe distance (3-4 seconds)\n7. Check right mirror - see entire vehicle\n8. Signal right\n9. Return to right lane smoothly'
          },
          {
            type: 'reminder',
            text: 'Move back to the right lane once you\'ve passed. The left lane is for passing, not cruising.'
          }
        ]
      },
      {
        id: 'hw-lesson-3',
        title: 'Exiting Highways Safely',
        duration: '10 min',
        content: [
          {
            type: 'concept',
            heading: 'Planning Your Exit',
            text: 'Proper exits require early planning and smooth deceleration. Missed exits can be recovered safely.'
          },
          {
            type: 'keypoints',
            points: [
              '🗺️ Know your exit: Plan ahead using GPS or signs',
              '👀 Watch for exit signs: Usually 2 km, 1 km, 500 m before exit',
              '➡️ Signal early: At least 5 seconds before moving right',
              '🚗 Move to right lane: Well before the exit ramp',
              '📉 Use deceleration lane: Slow down AFTER leaving highway',
              '❌ Never stop or reverse: If you miss exit, take the next one'
            ]
          },
          {
            type: 'example',
            scenario: 'Exiting the highway safely',
            action: '1. See your exit sign (1-2 km ahead)\n2. Check mirrors and begin moving right\n3. Signal right\n4. Move to rightmost lane\n5. Maintain highway speed until exit ramp\n6. Enter deceleration lane\n7. Gradually reduce speed to ramp limit\n8. Check speed - highway speed feels slow after long drive\n9. Follow ramp safely to surface street'
          },
          {
            type: 'reminder',
            text: 'Slow down gradually on the ramp. After driving at high speed, 50 km/h feels much slower than it actually is.'
          }
        ]
      },
      {
        id: 'hw-lesson-4',
        title: 'Highway Driving in Adverse Conditions',
        duration: '15 min',
        content: [
          {
            type: 'concept',
            heading: 'Adapting to Weather and Traffic',
            text: 'High speeds amplify the dangers of rain, fog, snow, and heavy traffic. Adjust your driving to match conditions.'
          },
          {
            type: 'keypoints',
            points: [
              '🌧️ Rain: Reduce speed, increase following distance to 4+ seconds',
              '🌫️ Fog: Low beams, slow down, use fog lights if equipped, pull off if visibility is near zero',
              '❄️ Snow/Ice: Reduce speed by 50% or more, 8-10 second following distance',
              '💨 Strong winds: Firm grip on wheel, slow down, expect gusts near overpasses',
              '🚦 Heavy traffic: Increase following distance, stay alert, avoid frequent lane changes',
              '🌙 Night: Use high beams when no traffic ahead, reduce speed slightly'
            ]
          },
          {
            type: 'example',
            scenario: 'Driving in heavy rain on the highway',
            action: '1. Turn on headlights (required by law in rain)\n2. Reduce speed by 10-20 km/h\n3. Increase following distance to 4-5 seconds\n4. Avoid sudden steering or braking\n5. Watch for standing water and hydroplaning\n6. If you hydroplane: ease off gas, hold wheel straight, don\'t brake\n7. Be extra cautious near transport trucks - spray reduces visibility'
          },
          {
            type: 'reminder',
            text: 'If conditions are too dangerous, exit the highway and wait it out. Arriving late is better than not arriving at all.'
          }
        ]
      }
    ],
    questions: [
      {
        id: 'highways-1',
        prompt: 'When merging onto a freeway you should',
        options: [
          'Stop at the end of the ramp and wait',
          'Match freeway speed in the acceleration lane and find a gap',
          'Drive on the shoulder until you see space',
          'Force entry with your horn'
        ],
        answerIndex: 1,
        explanation: 'Use the acceleration lane to match speed and merge into a gap without stopping traffic.'
      },
      {
        id: 'highways-2',
        prompt: 'If you miss your exit you should',
        options: [
          'Stop on the shoulder and back up',
          'Cross the median to turn around',
          'Take the next exit and reroute',
          'Stop in the lane with flashers'
        ],
        answerIndex: 2,
        explanation: 'Stay with traffic flow; exit at the next ramp and rejoin safely.'
      },
      {
        id: 'highways-3',
        prompt: 'In rain on the freeway you increase following distance because',
        options: [
          'Road spray reduces vision and traction',
          'Police may be nearby',
          'Fuel use is higher',
          'Traffic signs are harder to read'
        ],
        answerIndex: 0,
        explanation: 'Wet roads and spray lengthen stopping distance and hide brake lights ahead.'
      },
      {
        id: 'highways-4',
        prompt: 'When your vehicle has a flat tire on a freeway you should',
        options: [
          'Brake hard in your lane',
          'Signal, ease to the shoulder, and exit the vehicle on the passenger side',
          'Stop in a live lane for visibility',
          'Continue driving until the next exit regardless of the rim'
        ],
        answerIndex: 1,
        explanation: 'Move off the live lane, use hazards, and exit away from traffic before changing the tire or calling for help.'
      },
      {
        id: 'highways-5',
        prompt: 'A high-occupancy vehicle (HOV) lane usually requires',
        options: [
          'Two or more people or a qualifying vehicle during posted hours',
          'Headlights on high beam',
          'A special fuel type',
          'Driving below the limit'
        ],
        answerIndex: 0,
        explanation: 'HOV lanes prioritize carpools or allowed vehicles; check the sign for hours and rules.'
      }
    ]
  },
  {
    id: 'intersections',
    icon: '🔄',
    title: 'Intersections and Turns',
    summary: 'Read the intersection, choose the right lane, and turn with precision.',
    focus: 'Right-of-way and turning paths.',
    intro: 'Intersections are where crashes happen most often. Learn to read traffic lights and signs, understand who has the right-of-way, execute smooth turns, and handle roundabouts. These skills will help you navigate every type of crossing safely and legally.',
    lessons: [
      {
        id: 'int-lesson-1',
        title: 'Approaching and Scanning Intersections',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'Reading the Intersection',
            text: 'Most crashes happen at intersections. Proper approach and scanning can prevent collisions and save lives.'
          },
          {
            type: 'keypoints',
            points: [
              '👀 Scan early: Look left-right-left as you approach',
              '🚦 Identify controls: Traffic lights, signs, or uncontrolled',
              '🚗 Count lanes: Know which lane goes where',
              '👥 Watch for pedestrians: At crosswalks and corners',
              '🚴 Check for cyclists: Especially on your right when turning',
              '⚡ Prepare to stop: Cover brake, slow down if needed'
            ]
          },
          {
            type: 'example',
            scenario: 'Approaching a signalized intersection',
            action: '1. See the intersection 12-15 seconds ahead\n2. Identify the traffic light and current signal\n3. Check for advanced green or turn signals\n4. Scan for pedestrians in crosswalks\n5. Look for vehicles that might run the red\n6. Decide: green = proceed carefully, yellow = prepare to stop, red = stop\n7. Position in correct lane for your direction\n8. Proceed only when safe'
          },
          {
            type: 'reminder',
            text: 'Green light means you CAN go, not that you MUST go. Always verify the intersection is clear before entering.'
          }
        ]
      },
      {
        id: 'int-lesson-2',
        title: 'Making Safe Right Turns',
        duration: '10 min',
        content: [
          {
            type: 'concept',
            heading: 'Right Turn Technique',
            text: 'Right turns are common but require attention to pedestrians, cyclists, and proper lane positioning.'
          },
          {
            type: 'keypoints',
            points: [
              '➡️ Signal 30 meters (or 3-5 seconds) before turn',
              '🚗 Position: Stay in rightmost lane',
              '👁️ Check mirrors and right blind spot',
              '🚴 Watch for cyclists: Never cut them off',
              '📉 Slow to 15-20 km/h before turning',
              '🔄 Turn into the rightmost lane of target street',
              '👥 Yield to pedestrians in crosswalk'
            ]
          },
          {
            type: 'example',
            scenario: 'Right turn at a red light (where permitted)',
            action: '1. Signal right well in advance\n2. Move to rightmost lane\n3. Come to complete stop at stop line\n4. Check for "No Right on Red" signs\n5. Yield to all pedestrians\n6. Check left for oncoming traffic\n7. Check right for pedestrians again\n8. When clear, turn into rightmost lane\n9. Accelerate smoothly, cancel signal'
          },
          {
            type: 'reminder',
            text: 'Pedestrians in the crosswalk have absolute priority. Wait for them to completely cross your path.'
          }
        ]
      },
      {
        id: 'int-lesson-3',
        title: 'Making Safe Left Turns',
        duration: '15 min',
        content: [
          {
            type: 'concept',
            heading: 'Left Turn Technique',
            text: 'Left turns are the most dangerous maneuver because you cross oncoming traffic. Patience and judgment are critical.'
          },
          {
            type: 'keypoints',
            points: [
              '⬅️ Signal 30 meters before turn',
              '🚗 Position in leftmost lane (or left-turn lane)',
              '🚦 Protected arrow: You have right-of-way, oncoming traffic stopped',
              '🟢 Unprotected green: You must yield to oncoming traffic',
              '👥 Always yield to pedestrians',
              '📍 Wait in intersection: If waiting for gap, position wheels straight',
              '🔄 Complete turn into leftmost lane of target street'
            ]
          },
          {
            type: 'example',
            scenario: 'Unprotected left turn on green light',
            action: '1. Signal left, check mirrors\n2. Move into left turn lane\n3. Proceed into intersection when green\n4. Position center of vehicle in center of intersection\n5. Keep wheels straight (prevents push into oncoming traffic if hit from behind)\n6. Yield to all oncoming traffic\n7. Yield to pedestrians crossing your target street\n8. When gap appears, check once more, then turn quickly\n9. Accelerate into leftmost lane of target street\n10. Straighten, cancel signal'
          },
          {
            type: 'reminder',
            text: 'If the light turns yellow or red while you\'re in the intersection, complete your turn. You\'re legally in the intersection and must clear it.'
          }
        ]
      },
      {
        id: 'int-lesson-4',
        title: 'Navigating Roundabouts',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'Roundabout Rules',
            text: 'Roundabouts improve traffic flow and reduce serious crashes. Follow the rules and they\'re simple and safe.'
          },
          {
            type: 'keypoints',
            points: [
              '🔄 Traffic flows counterclockwise',
              '⚠️ Yield to traffic already in the circle',
              '👥 Yield to pedestrians in crosswalks',
              '🚫 Never stop inside the roundabout',
              '➡️ Signal right before your exit',
              '🚗 Stay in your lane throughout',
              '↩️ Missed exit? Go around again - never reverse'
            ]
          },
          {
            type: 'example',
            scenario: 'Entering and exiting a roundabout',
            action: '1. Slow down as you approach\n2. Check for pedestrians at crosswalk\n3. Yield to vehicles already circulating\n4. When gap appears, enter smoothly\n5. Stay in your lane (don\'t change lanes inside)\n6. Count exits to find yours\n7. Signal right before your exit\n8. Exit smoothly into correct lane\n9. Cancel signal'
          },
          {
            type: 'reminder',
            text: 'Multi-lane roundabouts: Use the left lane for left turns or U-turns, right lane for right turns or straight through. Follow lane markings.'
          }
        ]
      }
    ],
    questions: [
      {
        id: 'intersections-1',
        prompt: 'Turning right on a red light is allowed when',
        options: [
          'You stop fully, there is no sign prohibiting it, and the path is clear',
          'Cross traffic is heavy',
          'You slow but keep rolling',
          'The crosswalk is occupied'
        ],
        answerIndex: 0,
        explanation: 'Stop first, check for signs and people, then turn when clear.'
      },
      {
        id: 'intersections-2',
        prompt: 'For an unprotected left turn on green you must',
        options: [
          'Turn immediately before oncoming cars arrive',
          'Yield to oncoming traffic and pedestrians, then turn when a safe gap opens',
          'Stop in the crosswalk to wait',
          'Use your horn to claim the turn'
        ],
        answerIndex: 1,
        explanation: 'Without a green arrow you must yield until oncoming traffic and people clear.'
      },
      {
        id: 'intersections-3',
        prompt: 'At a four-way stop where two cars arrive together from opposite directions and one is turning left',
        options: [
          'The left-turning car goes first',
          'Both go at the same time',
          'The car going straight goes first',
          'The larger vehicle goes first'
        ],
        answerIndex: 2,
        explanation: 'When arrival is simultaneous, the driver going straight has the right-of-way.'
      },
      {
        id: 'intersections-4',
        prompt: 'Entering a roundabout you should',
        options: [
          'Yield to traffic already in the circle and to pedestrians',
          'Stop inside the circle to choose your exit',
          'Signal left until you exit',
          'Drive straight across the center island'
        ],
        answerIndex: 0,
        explanation: 'Yield to circulating traffic and people in crosswalks; enter when a gap appears.'
      },
      {
        id: 'intersections-5',
        prompt: 'Wide right turns should be started from',
        options: [
          'The left lane to give more swing',
          'The rightmost lane, staying close to the curb',
          'Any lane with space',
          'The shoulder'
        ],
        answerIndex: 1,
        explanation: 'Use the rightmost lane and track close to the curb to avoid cutting across lanes or cyclists.'
      }
    ]
  },
  {
    id: 'emergency',
    icon: '⚠️',
    title: 'Emergency and Challenging Conditions',
    summary: 'Stay calm under pressure and choose the safest escape path.',
    focus: 'Skids, low grip, and unexpected failures.',
    intro: 'Difficult situations test your skills. This section prepares you for brake failure, hydroplaning, skids on ice, fog, steep hills, and other challenges. You will learn prevention first, but also how to respond if something goes wrong, so you stay safe and in control.',
    lessons: [
      {
        id: 'em-lesson-1',
        title: 'Handling Vehicle Emergencies',
        duration: '15 min',
        content: [
          {
            type: 'concept',
            heading: 'When Things Go Wrong',
            text: 'Vehicle failures are rare but can be frightening. Knowing what to do before panic sets in can prevent a crash.'
          },
          {
            type: 'keypoints',
            points: [
              '🚨 Brake failure: Pump brakes, downshift, use parking brake gradually, aim for escape path',
              '🔧 Tire blowout: Firm grip, ease off gas, steer straight, brake gently when controlled',
              '⚡ Stuck accelerator: Shift to neutral, brake, steer to safety, turn off engine when stopped',
              '🔥 Engine fire: Pull over immediately, turn off engine, get everyone out, move away, call 911',
              '💨 Hood flies up: Look through gap or out side window, slow down, pull over safely',
              '❄️ Overheating: Turn off AC, turn on heater full blast, pull over when safe'
            ]
          },
          {
            type: 'example',
            scenario: 'Your brakes fail on a busy road',
            action: '1. Don\'t panic - you have options\n2. Pump the brake pedal rapidly\n3. Downshift to lower gear for engine braking\n4. Apply parking brake gradually (not full or you\'ll spin)\n5. Look for escape route (uphill, open space, gravel)\n6. Honk and use flashers to warn others\n7. If all else fails, sideswiping a guardrail or bushes is better than a head-on crash\n8. Once stopped, do not drive - call for tow'
          },
          {
            type: 'reminder',
            text: 'Regular maintenance prevents most emergencies. Check brakes, tires, and fluids regularly.'
          }
        ]
      },
      {
        id: 'em-lesson-2',
        title: 'Driving in Rain and Wet Conditions',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'Wet Road Hazards',
            text: 'Rain reduces traction and visibility. The first 10 minutes of rain are most dangerous as oil rises to the surface.'
          },
          {
            type: 'keypoints',
            points: [
              '👁️ Turn on headlights: Required by law in rain, improves visibility',
              '🐌 Slow down: Reduce speed by 10-20 km/h',
              '🚗 Increase following distance: 4-5 seconds minimum',
              '💦 Avoid standing water: Can cause hydroplaning',
              '🚫 No cruise control: Need active control in rain',
              '🛣️ Watch for pooling: Especially in right lane and dips',
              '🌪️ After heavy rain: Beware of flooded areas and debris'
            ]
          },
          {
            type: 'example',
            scenario: 'You hit a patch of standing water and start to hydroplane',
            action: '1. Stay calm, don\'t panic\n2. Ease off the gas pedal gently\n3. Do NOT brake\n4. Do NOT turn the steering wheel suddenly\n5. Hold steering wheel straight\n6. Let vehicle slow naturally until tires regain grip\n7. Once you feel traction return, steer gently\n8. Slow down for remaining journey'
          },
          {
            type: 'reminder',
            text: 'Hydroplaning occurs when water builds up faster than tires can push it away. Good tire tread and reduced speed prevent it.'
          }
        ]
      },
      {
        id: 'em-lesson-3',
        title: 'Winter Driving and Skid Control',
        duration: '15 min',
        content: [
          {
            type: 'concept',
            heading: 'Driving on Ice and Snow',
            text: 'Winter conditions demand major adjustments to speed, following distance, and steering inputs. Black ice is invisible and extremely dangerous.'
          },
          {
            type: 'keypoints',
            points: [
              '❄️ Reduce speed: By 50% or more on snow, to a crawl on ice',
              '⏱️ Increase following distance: 8-10 seconds',
              '🚗 Accelerate gently: Avoid wheel spin',
              '🛡️ Brake early and gently: Test brakes in safe area first',
              '🔄 If you skid: Steer where you want to go, ease off gas, don\'t brake hard',
              '🌡️ Black ice zones: Bridges, overpasses, shaded areas, early morning',
              '☁️ Clear all windows: Remove all snow from vehicle'
            ]
          },
          {
            type: 'example',
            scenario: 'Your vehicle starts to skid on ice',
            action: '1. Don\'t panic or overcorrect\n2. Ease off the gas pedal\n3. Do NOT slam on brakes\n4. Look where you want the vehicle to go\n5. Steer gently in that direction\n6. If rear skids right, steer right; if left, steer left\n7. As vehicle straightens, straighten steering wheel\n8. Once recovered, slow down significantly'
          },
          {
            type: 'reminder',
            text: 'ABS brakes: In emergency, press hard and hold - don\'t pump. Steering while braking is possible with ABS. Without ABS: pump brakes.'
          }
        ]
      },
      {
        id: 'em-lesson-4',
        title: 'Fog, Night, and Low Visibility Driving',
        duration: '10 min',
        content: [
          {
            type: 'concept',
            heading: 'Driving When You Can\'t See',
            text: 'Reduced visibility dramatically increases crash risk. Sometimes the safest decision is not to drive at all.'
          },
          {
            type: 'keypoints',
            points: [
              '🌫️ Fog - Use low beams: High beams reflect and blind you',
              '💡 Fog lights: Use if equipped, follow vehicle lights ahead at safe distance',
              '🚫 Never use parking lights only: Illegal and invisible',
              '🌙 Night - Use high beams: Except within 150m of oncoming traffic',
              '🚗 Reduce speed: You can only drive as fast as you can see',
              '❌ Don\'t follow taillights closely: They might stop suddenly',
              '🛣️ If visibility near zero: Pull completely off road and wait'
            ]
          },
          {
            type: 'example',
            scenario: 'Dense fog reduces visibility to 20 meters',
            action: '1. Slow down significantly\n2. Turn on low-beam headlights\n3. Use fog lights if equipped\n4. Turn on hazard flashers if going very slowly\n5. Use right edge line as guide\n6. If you can\'t see lane markings, it\'s too dangerous\n7. Pull completely off road to parking lot or rest area\n8. Turn off all lights except parking lights\n9. Wait until fog lifts'
          },
          {
            type: 'reminder',
            text: 'Other drivers might not slow down in fog. Getting rear-ended is a real risk - use flashers if moving slowly.'
          }
        ]
      }
    ],
    questions: [
      {
        id: 'emergency-1',
        prompt: 'If your brakes fail you should',
        options: [
          'Turn off the ignition while moving',
          'Downshift, pump the brakes, and use the parking brake gradually',
          'Drive faster to find help',
          'Open the doors for wind drag'
        ],
        answerIndex: 1,
        explanation: 'Engine braking plus gentle parking brake pressure helps you slow without a spin.'
      },
      {
        id: 'emergency-2',
        prompt: 'Hydroplaning is most likely when',
        options: [
          'Driving fast on standing water with worn tires',
          'Driving slowly in light rain',
          'On dry pavement',
          'With new tires at low speed'
        ],
        answerIndex: 0,
        explanation: 'Water, speed, and low tread depth make the tires ride on water instead of grip.'
      },
      {
        id: 'emergency-3',
        prompt: 'When you start to skid on ice, steer',
        options: [
          'Against the skid to straighten faster',
          'In the direction you want the front of the vehicle to go',
          'Not at all until the skid ends',
          'Fully to the left then right'
        ],
        answerIndex: 1,
        explanation: 'Look and steer where you want to go, then straighten smoothly as traction returns.'
      },
      {
        id: 'emergency-4',
        prompt: 'In dense fog the safest choice is to',
        options: [
          'Use high beams for more light',
          'Slow down, low beams on, and pull off safely if you cannot see lane markings',
          'Follow taillights closely',
          'Speed up to exit the fog faster'
        ],
        answerIndex: 1,
        explanation: 'Low beams cut glare; if you cannot see, leave the lane and wait in a safe area.'
      },
      {
        id: 'emergency-5',
        prompt: 'On a steep downhill in snow you should',
        options: [
          'Shift to a lower gear and brake gently in a straight line',
          'Shift to neutral and coast',
          'Turn the wheel side to side to slow',
          'Use cruise control to hold speed'
        ],
        answerIndex: 0,
        explanation: 'Engine braking plus light, straight-line braking keeps traction on a slippery descent.'
      }
    ]
  },
  {
    id: 'responsible',
    icon: '😊',
    title: 'Responsible Driving',
    summary: 'Make choices that keep you legal, rested, and distraction-free.',
    focus: 'Mindset, impairment, and courtesy.',
    intro: 'The smartest drivers know that safety begins before you start the car. This section covers ditching distractions, recognizing fatigue and impairment, respecting emergency vehicles, and planning ahead for a sober ride. Your mindset is your best safety tool.',
    lessons: [
      {
        id: 'rd-lesson-1',
        title: 'Avoiding Distractions',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'The Danger of Distracted Driving',
            text: 'Distracted driving kills. Taking your eyes off the road for just 2 seconds doubles your crash risk. At 50 km/h, you travel 28 meters in 2 seconds - the length of a basketball court - completely blind.'
          },
          {
            type: 'keypoints',
            points: [
              '📱 Phone use: Illegal and deadly - even hands-free reduces attention',
              '🎵 Infotainment: Set music, GPS, and climate before driving',
              '🍔 Eating/drinking: Wait until parked',
              '💄 Grooming: Never while driving',
              '👥 Passengers: Keep conversations calm, no roughhousing',
              '🐕 Pets: Secure in carriers or harnesses'
            ]
          },
          {
            type: 'example',
            scenario: 'Your phone rings while driving',
            action: '1. Do NOT answer or look at it\n2. Let it go to voicemail\n3. If urgent, pull over safely first\n4. Stop completely in parking lot or safe shoulder\n5. Put vehicle in Park, parking brake on\n6. Then use phone\n7. Better: enable Do Not Disturb mode before driving'
          },
          {
            type: 'reminder',
            text: 'No call, text, or notification is worth a life. If it\'s truly urgent, they\'ll call again. Pull over first.'
          }
        ]
      },
      {
        id: 'rd-lesson-2',
        title: 'Recognizing and Preventing Fatigue',
        duration: '10 min',
        content: [
          {
            type: 'concept',
            heading: 'Drowsy Driving is Impaired Driving',
            text: 'Being awake for 18 hours impairs you as much as a 0.05 blood alcohol level. Microsleeps - brief unconscious episodes - can occur without warning.'
          },
          {
            type: 'keypoints',
            points: [
              '😴 Warning signs: Yawning, heavy eyelids, wandering thoughts, drifting lanes',
              '🛑 Immediate action: Pull over and rest or switch drivers',
              '☕ Caffeine only delays: Coffee keeps you awake 30-60 min, then crashes harder',
              '🌙 Peak danger: Between 2-6 AM and 2-4 PM',
              '🚗 Long trips: Break every 2 hours or 200 km',
              '😴 Best solution: 20-minute power nap in safe location'
            ]
          },
          {
            type: 'example',
            scenario: 'You feel drowsy on a highway',
            action: '1. Recognize the danger immediately\n2. Signal and move to right lane\n3. Find next rest stop or safe exit\n4. Pull completely off the road\n5. Park in safe, well-lit area\n6. Set alarm for 20 minutes\n7. Nap or rest\n8. After rest, assess if you can continue safely\n9. If still tired, call someone or stay put'
          },
          {
            type: 'reminder',
            text: 'Rolling down windows, loud music, and cold air are NOT effective. Only sleep cures fatigue.'
          }
        ]
      },
      {
        id: 'rd-lesson-3',
        title: 'Understanding Impaired Driving',
        duration: '15 min',
        content: [
          {
            type: 'concept',
            heading: 'Impairment Comes in Many Forms',
            text: 'Alcohol, cannabis, prescription drugs, and over-the-counter medications can all impair your ability to drive safely. Zero impairment is the only safe level for new drivers.'
          },
          {
            type: 'keypoints',
            points: [
              '🍺 Alcohol: Zero tolerance for new drivers (GDL) - any amount = suspension',
              '🌿 Cannabis: Impairs reaction time, judgment, and coordination - illegal and dangerous',
              '💊 Medications: Read labels - many cause drowsiness or slow reactions',
              '⚖️ Legal consequences: License suspension, fines, jail time, criminal record',
              '🚗 Plan ahead: Designated driver, taxi, rideshare, public transit, stay overnight',
              '❌ Myths: Coffee, food, and showers do NOT sober you up'
            ]
          },
          {
            type: 'example',
            scenario: 'Planning a night out',
            action: '1. Before leaving home, decide: Will I drink?\n2. If yes, arrange alternative transportation NOW\n3. Give car keys to sober friend\n4. Use rideshare app, taxi, or public transit\n5. Stay at friend\'s place\n6. If plans change, NEVER drive impaired\n7. Cost of taxi << cost of DUI or crash\n8. Next day: ensure you\'re fully sober before driving'
          },
          {
            type: 'reminder',
            text: 'Impaired driving is 100% preventable. Make the safe choice BEFORE you start drinking or using substances.'
          }
        ]
      },
      {
        id: 'rd-lesson-4',
        title: 'Yielding to Emergency Vehicles',
        duration: '8 min',
        content: [
          {
            type: 'concept',
            heading: 'Emergency Vehicle Right-of-Way',
            text: 'When an ambulance, fire truck, or police vehicle approaches with lights and sirens, you must yield immediately. Seconds can mean the difference between life and death.'
          },
          {
            type: 'keypoints',
            points: [
              '🚨 Recognize: Flashing lights (red, blue, white) and sirens',
              '➡️ Pull right: Move to the right curb or edge and stop',
              '🛑 Stop completely: Don\'t just slow down',
              '🚦 At intersections: Don\'t block intersection, pull to right after clearing',
              '🚫 Never follow: Stay 150 meters back',
              '⚠️ Move Over Law: Slow down and move over for stopped emergency vehicles'
            ]
          },
          {
            type: 'example',
            scenario: 'Ambulance approaching from behind with sirens',
            action: '1. Check mirrors to confirm direction\n2. Signal right\n3. Check blind spot\n4. Move to rightmost position safely\n5. Stop completely\n6. Wait for emergency vehicle to pass\n7. Check that no more are coming\n8. Signal, check mirrors, re-enter traffic\n9. Resume normal driving'
          },
          {
            type: 'reminder',
            text: 'Never slam on brakes in front of an emergency vehicle. Move right smoothly and stop. They\'ll go around you.'
          }
        ]
      }
    ],
    questions: [
      {
        id: 'responsible-1',
        prompt: 'Using a handheld phone while driving',
        options: [
          'Is safe if traffic is light',
          'Raises crash risk and is often illegal',
          'Is safer at high speed',
          'Is fine if you use the horn'
        ],
        answerIndex: 1,
        explanation: 'Distraction is a leading crash factor; most places ban handheld use while moving.'
      },
      {
        id: 'responsible-2',
        prompt: 'If you feel drowsy you should',
        options: [
          'Open the window and keep driving',
          'Pull over to rest or switch drivers',
          'Follow a truck closely to stay alert',
          'Turn up the music and speed up'
        ],
        answerIndex: 1,
        explanation: 'Micro-sleeps are deadly; stop to rest or swap before you lose awareness.'
      },
      {
        id: 'responsible-3',
        prompt: 'After taking medication that causes drowsiness you should',
        options: [
          'Drive only on back roads',
          'Wait until you know its effect or choose a different ride',
          'Drink coffee and continue',
          'Drive faster to finish sooner'
        ],
        answerIndex: 1,
        explanation: 'If a drug can impair you, delay driving or find another way to travel.'
      },
      {
        id: 'responsible-4',
        prompt: 'When an emergency vehicle approaches with lights and siren you must',
        options: [
          'Maintain speed to avoid blocking',
          'Pull to the nearest curb or shoulder and stop when safe',
          'Follow the emergency vehicle closely',
          'Stop in the lane immediately'
        ],
        answerIndex: 1,
        explanation: 'Yield by moving to the edge and stopping so responders get a clear path.'
      },
      {
        id: 'responsible-5',
        prompt: 'Planning a sober ride matters because',
        options: [
          'It saves fuel',
          'It prevents impairment before it starts and keeps you legal',
          'Police ignore tired drivers',
          'Impairment only affects high speeds'
        ],
        answerIndex: 1,
        explanation: 'Arranging a ride or staying put removes the risk and legal consequences of impaired driving.'
      }
    ]
  },
  {
    id: 'sharing',
    icon: '👥',
    title: 'Sharing the Road',
    summary: 'Make space for people walking, biking, riding, or driving big vehicles.',
    focus: 'Safe passing and yielding habits.',
    intro: 'You are never alone on the road. Cyclists, pedestrians, motorcycles, and large trucks all share the same space and have different needs. Learn to give cyclists space, stop for school buses, respect truck limitations, and always yield to people crossing. Courtesy saves lives.',
    lessons: [
      {
        id: 'sr-lesson-1',
        title: 'Sharing the Road with Cyclists',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'Cyclists are Vehicles Too',
            text: 'Bicycles have the same rights and responsibilities as motor vehicles. They are vulnerable and deserve space and respect.'
          },
          {
            type: 'keypoints',
            points: [
              '🚴 Give space: Pass with at least 1 meter clearance (required by law in many places)',
              '👁️ Before turning right: Check mirror and blind spot for cyclists',
              '🚪 Opening doors: Check mirror and blind spot (dooring causes serious injuries)',
              '🛑 Never squeeze: Wait until safe to pass with full clearance',
              '🚦 At intersections: Cyclists may take full lane - let them',
              '🚗 Bike lanes: Never drive, park, or stop in bike lanes',
              '⚠️ Expect the unexpected: Cyclists may swerve for potholes or debris'
            ]
          },
          {
            type: 'example',
            scenario: 'Passing a cyclist on a narrow road',
            action: '1. Slow down as you approach\n2. Check oncoming traffic\n3. Check that cyclist is riding predictably\n4. Signal left if changing lanes\n5. Move completely into adjacent lane if possible\n6. If no lane available, wait until clear\n7. Pass with 1+ meter of space\n8. Do NOT honk unless warning of danger\n9. Return to lane only when cyclist is well behind'
          },
          {
            type: 'reminder',
            text: 'Right hooks kill cyclists. Before turning right, always check your mirror and blind spot. If a cyclist is there, yield and let them go straight first.'
          }
        ]
      },
      {
        id: 'sr-lesson-2',
        title: 'Protecting Pedestrians',
        duration: '10 min',
        content: [
          {
            type: 'concept',
            heading: 'Pedestrians are Vulnerable',
            text: 'In a car-pedestrian collision, the pedestrian always loses. They have absolute right-of-way in crosswalks.'
          },
          {
            type: 'keypoints',
            points: [
              '🚪🏻 Crosswalks: Yield to pedestrians, even at unmarked intersections',
              '🛑 Stop completely: Don\'t inch forward while they cross',
              '👥 Watch for children: Unpredictable movements near schools and parks',
              '👴 Seniors: May need more time to cross',
              '♿️ Accessibility: Yield extra time and space',
              '🌙 Night: Pedestrians harder to see - slow down in areas they frequent',
              '🚗 Backing up: Always check behind before reversing'
            ]
          },
          {
            type: 'example',
            scenario: 'Pedestrian steps into crosswalk as you approach',
            action: '1. Begin braking immediately\n2. Stop well before the crosswalk\n3. Make eye contact with pedestrian\n4. Wave them across if needed\n5. Do NOT wave them to hurry\n6. Stay stopped until they completely clear your lane\n7. Check for more pedestrians\n8. Proceed only when crosswalk is clear'
          },
          {
            type: 'reminder',
            text: 'School zones and playground zones have reduced speed limits during posted hours. Fines are doubled. Watch for children.'
          }
        ]
      },
      {
        id: 'sr-lesson-3',
        title: 'Sharing the Road with Large Trucks',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'Truck Limitations',
            text: 'Large trucks have massive blind spots, need huge stopping distances, and require wide turning space. Give them room.'
          },
          {
            type: 'keypoints',
            points: [
              '🚫 No-Zones: Blind spots directly behind, directly in front, and along both sides',
              '👁️ "If you can\'t see the driver\'s mirror, they can\'t see you"',
              '🚦 Never cut in front: Trucks need 2-3× stopping distance of cars',
              '🔄 Wide turns: Trucks swing wide right to turn right - give space',
              '⏬ Don\'t pass on right: Especially during right turns',
              '⛰️ Downhill: Trucks go slower - be patient',
              '💨 Passing: Pass quickly and completely, signal before returning'
            ]
          },
          {
            type: 'example',
            scenario: 'Truck ahead is turning right at an intersection',
            action: '1. Notice truck is signaling right\n2. Truck swings left first to make room\n3. Do NOT squeeze up on the right side\n4. Do NOT try to pass on the right\n5. Stay back and wait\n6. Watch for rear wheels cutting corner\n7. Proceed only when truck completes turn'
          },
          {
            type: 'reminder',
            text: 'Tailgating a truck is deadly. You can\'t see ahead, and if they brake suddenly, you\'ll go under the trailer. Stay back 4-6 seconds.'
          }
        ]
      },
      {
        id: 'sr-lesson-4',
        title: 'Motorcycles and School Buses',
        duration: '10 min',
        content: [
          {
            type: 'concept',
            heading: 'Special Vehicles, Special Care',
            text: 'Motorcycles are small and fast. School buses carry children. Both require extra attention and caution.'
          },
          {
            type: 'keypoints',
            points: [
              '🏍️ Motorcycles: Harder to see, easier to misjudge distance and speed',
              '👁️ Check twice: Before changing lanes or turning',
              '🌧️ Weather: Motorcycles are more affected by wind and rain',
              '🚌 School buses with flashing red lights: STOP (both directions on undivided roads)',
              '🛑 Stay stopped: Until lights stop flashing and children are safe',
              '🚨 Passing stopped bus: Illegal and extremely dangerous ($400-$2000 fine)',
              '📍 Give extra room: To both motorcycles and buses'
            ]
          },
          {
            type: 'example',
            scenario: 'School bus ahead activates flashing red lights',
            action: '1. See flashing amber lights first (warning)\n2. Slow down and prepare to stop\n3. Red lights flash and stop arm extends\n4. Stop at least 5 meters back\n5. Stay stopped even if you don\'t see children\n6. Watch both sides of the road\n7. Wait until lights stop flashing\n8. Wait until stop arm retracts\n9. Proceed cautiously, watching for children'
          },
          {
            type: 'reminder',
            text: 'Motorcycles have the same rights as cars. Give them a full lane - don\'t share. Children are unpredictable - always stop for school buses.'
          }
        ]
      }
    ],
    questions: [
      {
        id: 'sharing-1',
        prompt: 'When passing a cyclist you should',
        options: [
          'Leave at least one metre of space where law requires',
          'Honk and pass closely to reduce time beside them',
          'Pass in the same lane with no gap',
          'Speed up on gravel to pass faster'
        ],
        answerIndex: 0,
        explanation: 'Give a wide buffer; many areas mandate one metre or more for safety.'
      },
      {
        id: 'sharing-2',
        prompt: 'You must stop for a school bus with flashing red lights when',
        options: [
          'You are behind it on an undivided road',
          'You are on the opposite side of any multi-lane road',
          'It is parked with no lights',
          'You are on a divided highway going the other way'
        ],
        answerIndex: 0,
        explanation: 'On undivided roads traffic both ways must stop for flashing red lights until they stop flashing.'
      },
      {
        id: 'sharing-3',
        prompt: 'Large trucks need extra room because',
        options: [
          'They turn and stop like cars',
          'They have large blind spots and longer stopping distances',
          'Their brakes are stronger than cars',
          'They always see you in mirrors'
        ],
        answerIndex: 1,
        explanation: 'Stay out of no-zones and avoid cutting in; trucks take longer to slow and to turn.'
      },
      {
        id: 'sharing-4',
        prompt: 'Motorcycles may appear farther away because',
        options: [
          'They are louder',
          'They are smaller and harder to judge',
          'They always ride faster than cars',
          'They use high beams'
        ],
        answerIndex: 1,
        explanation: 'Small size tricks your depth perception; wait for a clear gap before turning.'
      },
      {
        id: 'sharing-5',
        prompt: 'At an intersection with a pedestrian in the crosswalk you should',
        options: [
          'Yield until the pedestrian has cleared your lane',
          'Proceed if they are on the other half of the road',
          'Drive around them slowly',
          'Honk to speed them up'
        ],
        answerIndex: 0,
        explanation: 'Yield to people until they are clear of your lane and adjacent lane you will enter.'
      }
    ]
  },
  {
    id: 'law',
    icon: '⚖️',
    title: 'Driving with the Law',
    summary: 'Know the paperwork, signals, and stops officers expect from you.',
    focus: 'Legal obligations and compliance.',
    intro: 'Driving is a privilege backed by law. You need to carry the right documents, understand what happens when you are stopped, know the consequences of breaking the rules, and learn how to interact with law enforcement. These requirements exist to keep roads safe and fair.',
    lessons: [
      {
        id: 'law-lesson-1',
        title: 'Required Documents and Licensing',
        duration: '10 min',
        content: [
          {
            type: 'concept',
            heading: 'What You Must Carry',
            text: 'Every time you drive, you must have specific documents with you. Failure to produce them can result in fines and vehicle impoundment.'
          },
          {
            type: 'keypoints',
            points: [
              '💳 Driver\'s license: Valid and appropriate class for vehicle',
              '📄 Vehicle registration: Proof of ownership/registration',
              '🛡️ Insurance: Proof of valid insurance coverage',
              '🔴 Red-bordered "N" (Novice): Must display if GDL Class 7',
              '⏰ License restrictions: Zero alcohol, passenger limits, time limits',
              '📱 Digital documents: Accepted in some jurisdictions, check local laws',
              '⚠️ Expired documents: Same as no documents - illegal'
            ]
          },
          {
            type: 'example',
            scenario: 'Police officer asks for your documents',
            action: '1. Pull over safely when signaled\n2. Turn off engine, roll down window\n3. Keep hands visible on steering wheel\n4. Wait for officer to approach\n5. Politely greet officer\n6. Provide license, registration, insurance\n7. Answer questions respectfully\n8. Accept any ticket or warning\n9. Wait for officer to return to vehicle before leaving'
          },
          {
            type: 'reminder',
            text: 'Keep insurance and registration in glove box. Always tell officer before reaching for it to avoid misunderstandings.'
          }
        ]
      },
      {
        id: 'law-lesson-2',
        title: 'Understanding Traffic Violations',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'Tickets, Fines, and Demerit Points',
            text: 'Traffic violations have real consequences: fines, demerit points, increased insurance, and potential license suspension.'
          },
          {
            type: 'keypoints',
            points: [
              '💸 Fines: Vary by violation ($100-$2000+), doubled in construction zones',
              '❌ Demerit points: Accumulate for moving violations (2-6 points each)',
              '🚫 Suspension: Too many points = license suspension',
              '📈 Insurance: Violations increase rates for 3+ years',
              '⚖️ Court: Right to dispute tickets in traffic court',
              '🚨 Criminal charges: Impaired, dangerous driving, hit-and-run',
              '📝 Pay or dispute: Must respond to ticket within deadline'
            ]
          },
          {
            type: 'example',
            scenario: 'Common violations and their consequences',
            action: 'Speeding 20 over: $138 fine + 2 points\nRunning red light: $167 fine + 2 points\nDistracted driving (phone): $368 fine + 4 points\nNo insurance: $598 fine + vehicle impound\nImpaired (GDL): Immediate suspension + criminal charges\nStunt driving (50+ over): License suspension + vehicle impound + up to $10,000 fine'
          },
          {
            type: 'reminder',
            text: 'Novice (GDL) drivers face stricter penalties. Any alcohol, any demerit points can trigger immediate suspension.'
          }
        ]
      },
      {
        id: 'law-lesson-3',
        title: 'Police Stops and Interactions',
        duration: '10 min',
        content: [
          {
            type: 'concept',
            heading: 'How to Handle a Traffic Stop',
            text: 'Being stopped by police can be stressful. Knowing what to expect and how to behave keeps everyone safe.'
          },
          {
            type: 'keypoints',
            points: [
              '🚨 When signaled: Pull over safely and promptly',
              '🛑 Stop completely: Turn off engine, lower window',
              '👋 Hands visible: Keep on steering wheel',
              '👮 Be respectful: "Yes, officer" / "No, officer"',
              '📄 Announce movements: "I\'m reaching for my registration in the glove box"',
              '❌ Don\'t argue: Save disputes for court',
              '📝 Accept ticket: Signing is NOT admitting guilt, just acknowledging receipt'
            ]
          },
          {
            type: 'example',
            scenario: 'You are pulled over for speeding',
            action: '1. See lights, safely reduce speed\n2. Signal right, check mirrors\n3. Pull to right shoulder or side street\n4. Stop completely, engine off\n5. Roll down window, hands on wheel\n6. Wait for officer\n7. "Good evening, officer"\n8. Provide documents when asked\n9. Answer questions honestly and briefly\n10. Accept ticket respectfully\n11. Wait for clearance to leave'
          },
          {
            type: 'reminder',
            text: 'You can dispute any ticket in court. But arguing roadside accomplishes nothing and could make things worse.'
          }
        ]
      },
      {
        id: 'law-lesson-4',
        title: 'Collision Reporting and Responsibilities',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'After a Crash',
            text: 'What you do after a collision is legally required. Leaving the scene is a serious criminal offense.'
          },
          {
            type: 'keypoints',
            points: [
              '🛑 STOP: Never leave the scene',
              '🩺 Help injured: Call 911 if anyone is hurt',
              '⚠️ Protect scene: Hazard lights, warning triangle if safe',
              '📄 Exchange info: Names, licenses, insurance, contact info',
              '📸 Document: Photos of damage, positions, scene',
              '👮 Police report: Required if injury, $2000+ damage, or hit-and-run',
              '📞 Notify insurer: Within 24 hours',
              '✍️ Write it down: Your account while memory is fresh'
            ]
          },
          {
            type: 'example',
            scenario: 'You are in a minor fender-bender',
            action: '1. Stop immediately, engine off\n2. Check for injuries - call 911 if needed\n3. Turn on hazard lights\n4. Move vehicles out of traffic if safe and minor\n5. Call police if required\n6. Exchange information with other driver\n7. Take photos: vehicles, damage, license plates, scene\n8. Get witness names and contact info\n9. Do NOT admit fault\n10. Call your insurance company\n11. Fill out collision report within 24 hours'
          },
          {
            type: 'reminder',
            text: 'Leaving the scene (hit-and-run) can result in criminal charges, jail time, and permanent license loss - even for minor damage.'
          }
        ]
      }
    ],
    questions: [
      {
        id: 'law-1',
        prompt: 'You must carry proof of',
        options: [
          'Insurance and vehicle registration while driving',
          'Only insurance',
          'Only a phone-based ID',
          'Nothing if the car is new'
        ],
        answerIndex: 0,
        explanation: 'Most regions require licence, registration, and insurance proof on request.'
      },
      {
        id: 'law-2',
        prompt: 'If stopped by police you should',
        options: [
          'Pull over safely, stay calm, and keep hands visible',
          'Exit and walk to the officer',
          'Reach quickly into the glove box before they arrive',
          'Argue from the driver seat while rolling'
        ],
        answerIndex: 0,
        explanation: 'Stopping safely with visible hands keeps everyone calm and safe.'
      },
      {
        id: 'law-3',
        prompt: 'Speeding fines and demerits matter because',
        options: [
          'They are suggestions only',
          'They can raise insurance costs and suspend your licence',
          'They apply only to commercial drivers',
          'They expire overnight'
        ],
        answerIndex: 1,
        explanation: 'Penalties affect your record, insurance, and privilege to drive.'
      },
      {
        id: 'law-4',
        prompt: 'Yielding to emergency vehicles with lights and sirens is required',
        options: [
          'Only on highways',
          'Only for ambulances',
          'On any road until the vehicle has passed',
          'Only at night'
        ],
        answerIndex: 2,
        explanation: 'The law requires yielding to authorized emergency vehicles wherever you meet them.'
      },
      {
        id: 'law-5',
        prompt: 'Failing to stop after a crash you are involved in can lead to',
        options: [
          'No consequences if damage is minor',
          'Serious charges and licence loss',
          'Only a warning',
          'A free repair'
        ],
        answerIndex: 1,
        explanation: 'Leaving a collision scene is a serious offence; stop, stay, and exchange information.'
      }
    ]
  },
  {
    id: 'towing',
    icon: '🚚',
    title: 'Towing a Trailer',
    summary: 'Balance weight, brake early, and control sway when pulling extra mass.',
    focus: 'Setup and handling with a trailer.',
    intro: "Towing a trailer changes how your vehicle handles. You'll learn how to distribute weight for stability, how to check safety gear before you leave, how to respond if the trailer starts to sway, and how to brake and descend hills safely with extra mass behind you. These skills are essential if you plan to tow.",
    lessons: [
      {
        id: 'tow-lesson-1',
        title: 'Trailer Hitching and Weight Distribution',
        duration: '15 min',
        content: [
          {
            type: 'concept',
            heading: 'Proper Trailer Setup',
            text: 'Correct hitching and weight distribution prevents sway, improves control, and keeps you legal. A poorly loaded trailer is dangerous.'
          },
          {
            type: 'keypoints',
            points: [
              '⚖️ Tongue weight: Should be 10-15% of total trailer weight',
              '🔗 Hitch class: Must match or exceed trailer weight',
              '⛓️ Secure ball: Proper size, locked coupler, safety pin in place',
              '🔗 Safety chains: Crossed under hitch in an X pattern',
              '🔌 Electrical: Test lights, brakes, and turn signals',
              '📦 Load distribution: Heavy items low and centered, secure all cargo',
              '🚨 Breakaway cable: Attached to vehicle frame, not hitch ball'
            ]
          },
          {
            type: 'example',
            scenario: 'Hitching a trailer before a trip',
            action: '1. Back vehicle to trailer (use spotter if possible)\n2. Align ball under coupler\n3. Lower coupler onto ball\n4. Lock coupler and insert safety pin\n5. Attach safety chains in X pattern\n6. Connect electrical plug\n7. Connect breakaway cable to frame\n8. Raise jack fully\n9. Test all lights and signals\n10. Check tongue weight - should not lift vehicle rear\n11. Final walk-around check'
          },
          {
            type: 'reminder',
            text: 'Too much tongue weight overloads the rear axle. Too little causes fishtailing. Aim for 10-15% on the hitch.'
          }
        ]
      },
      {
        id: 'tow-lesson-2',
        title: 'Driving Techniques with a Trailer',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'How Towing Changes Driving',
            text: 'A trailer makes your vehicle longer, heavier, and slower to respond. Every maneuver requires more time and space.'
          },
          {
            type: 'keypoints',
            points: [
              '🚗 Acceleration: Slower and gentler',
              '🛑 Braking: Start earlier, brake longer',
              '⏱️ Following distance: Double normal distance (4 seconds minimum)',
              '🔄 Turns: Wider turns, slower speeds (10-15 km/h)',
              '🌪️ Wind: More affected by side winds and passing trucks',
              '⛰️ Hills: Use lower gear, maintain speed',
              '🔍 Mirrors: Check extended mirrors frequently, know trailer position'
            ]
          },
          {
            type: 'example',
            scenario: 'Making a right turn with a trailer',
            action: '1. Signal early (50+ meters)\n2. Position wide in lane (but don\'t cross center)\n3. Check mirrors - know where trailer is\n4. Slow to 10-15 km/h\n5. Begin turn, watching right mirror\n6. Turn wider than normal\n7. Watch for trailer wheels cutting corner\n8. Straighten gradually\n9. Accelerate gently\n10. Cancel signal'
          },
          {
            type: 'reminder',
            text: 'Trailer wheels track inside your vehicle wheels on turns. Swing wide to prevent trailer from hitting curbs or obstacles.'
          }
        ]
      },
      {
        id: 'tow-lesson-3',
        title: 'Preventing and Correcting Trailer Sway',
        duration: '12 min',
        content: [
          {
            type: 'concept',
            heading: 'Understanding Trailer Sway',
            text: 'Sway occurs when the trailer moves side-to-side behind your vehicle. If not corrected, it can cause loss of control and rollover.'
          },
          {
            type: 'keypoints',
            points: [
              '⚠️ Causes: Improper loading, crosswinds, passing trucks, too much speed, worn tires',
              '🚨 Prevention: Proper tongue weight, load heavy items forward and low, reduce speed',
              '🛑 If sway starts: Take foot OFF gas, do NOT brake, hold wheel straight',
              '🎯 Sway control: Apply trailer brakes only (if equipped) to straighten',
              '🚫 Never accelerate: Makes sway worse',
              '❄️ Severe sway: Let vehicle slow naturally until sway stops',
              '🔧 After recovery: Pull over and check load, tire pressure, hitch'
            ]
          },
          {
            type: 'example',
            scenario: 'Trailer begins to sway on the highway',
            action: '1. Don\'t panic\n2. Grip steering wheel firmly\n3. Take foot completely OFF gas pedal\n4. Do NOT touch brake pedal\n5. Hold steering wheel straight\n6. If equipped, activate trailer brake controller manually\n7. Let vehicle slow down naturally\n8. Keep steering straight as sway decreases\n9. Once sway stops, slow down further\n10. Pull over safely and inspect everything'
          },
          {
            type: 'reminder',
            text: 'Braking during sway can jackknife the trailer. Ease off gas and keep steering straight - let physics slow you down.'
          }
        ]
      },
      {
        id: 'tow-lesson-4',
        title: 'Backing Up and Parking with a Trailer',
        duration: '15 min',
        content: [
          {
            type: 'concept',
            heading: 'The Art of Backing a Trailer',
            text: 'Backing with a trailer is counterintuitive. The trailer goes opposite to your steering. Practice makes perfect.'
          },
          {
            type: 'keypoints',
            points: [
              '🔄 Basic rule: Turn wheel OPPOSITE to where you want trailer to go',
              '👁️ Use mirrors: Watch both sides of trailer',
              '🐌 Go SLOW: Walking pace or slower',
              '✋ Hand at 6 o\'clock: Move hand left = trailer goes left (for beginners)',
              '👥 Get help: Have spotter guide you',
              '↩️ Pull forward often: Restart if alignment is off',
              '🎯 Practice: Empty parking lot until it feels natural'
            ]
          },
          {
            type: 'example',
            scenario: 'Backing trailer into campsite on right side',
            action: '1. Pull past site on left side\n2. Stop when trailer rear is even with entrance\n3. Put vehicle in reverse\n4. Place hand at bottom of steering wheel (6 o\'clock)\n5. To move trailer right, move hand right\n6. Slowly back while turning wheel right\n7. Watch right mirror - trailer should angle right\n8. When trailer is angled toward site, straighten wheel\n9. Back straight until another correction needed\n10. Use small steering corrections\n11. Pull forward and restart if you get off track'
          },
          {
            type: 'reminder',
            text: 'Longer trailers respond more slowly. Shorter trailers turn sharper and need quicker corrections. Practice with YOUR trailer.'
          }
        ]
      }
    ],
    questions: [
      {
        id: 'towing-1',
        prompt: 'Proper tongue weight should be about',
        options: [
          '10-15% of trailer weight on the hitch',
          '1% so the trailer is light',
          '40% for maximum grip',
          '0% with weight behind the axle'
        ],
        answerIndex: 0,
        explanation: 'Keeping roughly 10-15% on the hitch reduces sway and keeps steering stable.'
      },
      {
        id: 'towing-2',
        prompt: 'Before towing you should always',
        options: [
          'Disable trailer lights to save power',
          'Check lights, safety chains, and tire pressure on both units',
          'Load heavy items at the very back',
          'Skip mirrors if you know the route'
        ],
        answerIndex: 1,
        explanation: 'Lights, chains, and correct tire pressure are critical for control and legality.'
      },
      {
        id: 'towing-3',
        prompt: 'If trailer sway begins you should',
        options: [
          'Accelerate hard to straighten it',
          'Steer sharply against the sway',
          'Hold the wheel steady, ease off the throttle, and gently apply trailer brakes if equipped',
          'Brake hard in a curve'
        ],
        answerIndex: 2,
        explanation: 'Stay straight, reduce speed smoothly, and use trailer brakes to pull the trailer back in line.'
      },
      {
        id: 'towing-4',
        prompt: 'On long descents while towing',
        options: [
          'Use a lower gear early and brake in short, firm intervals',
          'Use cruise control to hold speed',
          'Shift to neutral to save fuel',
          'Ride the brakes lightly the whole way'
        ],
        answerIndex: 0,
        explanation: 'Engine braking prevents brake fade; pulse braking keeps brakes cooler.'
      },
      {
        id: 'towing-5',
        prompt: 'Safe lane changes while towing require',
        options: [
          'Short gaps because the trailer follows tightly',
          'Extra space, early signals, and wide mirror checks',
          'Turning off turn signals to avoid confusion',
          'Relying on blind spots staying the same as without a trailer'
        ],
        answerIndex: 1,
        explanation: 'Longer length means you need more room and earlier communication before moving over.'
      }
    ]
  }
];
